/**
 * Stakeholder Management Page
 * Manages Investors, Brokers, and Collateral Providers
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Business as BusinessIcon,
  Email as EmailIcon,
  Search as SearchIcon,
  MergeType as MergeTypeIcon,
  RestartAlt as RestartAltIcon,
} from '@mui/icons-material';
import { apiGet, apiDelete, apiPut } from '../utils/apiClient';
import useAsyncData from '../hooks/useAsyncData';
import { usePermissions } from '../contexts/PermissionContext';
import { MODULES, PERMISSION_LEVELS } from '../contexts/PermissionConstants';
import ConfirmDialog from '../components/common/ConfirmDialog';
import DataTable from '../components/common/DataTable';
import StakeholderFormDialog from '../components/common/StakeholderFormDialog';
import MergeInvestorDialog from '../components/common/MergeInvestorDialog';
import useConfirmDialog from '../hooks/useConfirmDialog';
import { useNotification } from '../contexts/NotificationContext';
import useCurrencyFormatter from '../hooks/useCurrencyFormatter';
import { useTenant } from '../contexts/TenantContext';
import usePagination from '../hooks/usePagination';
import useTableSort from '../hooks/useTableSort';
import { getFicaColor, getLicenseColor } from '../utils/statusColors';
import logger from '../utils/logger';


/**
 * Shared filter toolbar for every stakeholder tab: search box + multi-select entity-type
 * chips + a domicile-country dropdown + a Reset. Chips are derived per tab from the data
 * actually present, so each stakeholder kind shows only its own real type values. All
 * filtering is client-side (see the filtered* memos). Mirrors the chip/Select pattern in
 * ContractList.js so it reads consistently with the rest of the app.
 */
function StakeholderFilterBar({
  searchTerm,
  onSearch,
  searchPlaceholder,
  typeOptions,
  selectedTypes,
  onToggleType,
  countries,
  countryValue,
  onCountry,
  onReset,
  actions,
}) {
  const filtersActive = searchTerm || selectedTypes.length > 0 || countryValue !== 'all';
  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            label="Search"
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => onSearch(e.target.value)}
            sx={{ minWidth: 250 }}
            placeholder={searchPlaceholder}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Country</InputLabel>
            <Select
              value={countryValue}
              onChange={(e) => onCountry(e.target.value)}
              label="Country"
            >
              <MenuItem value="all">All countries</MenuItem>
              {countries.map((c) => (
                <MenuItem key={c.id} value={c.id}>{c.country}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        {actions}
      </Box>
      {(typeOptions.length > 0 || filtersActive) && (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mt: 1.5 }}>
          {typeOptions.map((t) => {
            const selected = selectedTypes.includes(t);
            return (
              <Chip
                key={t}
                label={t}
                color={selected ? 'primary' : 'default'}
                variant={selected ? 'filled' : 'outlined'}
                onClick={() => onToggleType(t)}
                clickable
              />
            );
          })}
          {filtersActive && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<RestartAltIcon />}
              onClick={onReset}
              sx={{ ml: 'auto' }}
            >
              Reset
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
}

function StakeholderManagement() {
  const { hasPermission } = usePermissions();
  const { isFeatureEnabled, getStakeholderLabel } = useTenant();
  const issuerLabel = getStakeholderLabel('stakeholder.issuer.label', 'Issuer');

  // ─── Currency formatter (tenant precision, ZAR — stakeholder exposure) ───────
  const { formatCurrency } = useCurrencyFormatter('ZAR');
  const [tabKey, setTabKey] = useState('investors');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [stakeholderType, setStakeholderType] = useState('investor');
  const [dialogMode, setDialogMode] = useState('edit');
  const confirm = useConfirmDialog();
  const notify = useNotification();
  const [searchTerm, setSearchTerm] = useState('');
  // Entity-type chips (multi-select, OR) + domicile-country dropdown. Shared across tabs
  // and reset on tab change, since only the active tab is visible at a time.
  const [typeFilters, setTypeFilters] = useState([]);
  const [countryFilter, setCountryFilter] = useState('all');
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);

  const canWrite = hasPermission(MODULES.STAKEHOLDER_MANAGEMENT, PERMISSION_LEVELS.WRITE);
  const canAdmin = hasPermission(MODULES.STAKEHOLDER_MANAGEMENT, PERMISSION_LEVELS.ADMIN);

  const brokersEnabled = isFeatureEnabled('stakeholder_management', 'brokers');
  const collateralEnabled = isFeatureEnabled('stakeholder_management', 'collateral');
  const issuersEnabled = isFeatureEnabled('stakeholder_management', 'issuers');

  // Investors
  const investorPagination = usePagination({ defaultRowsPerPage: 10 });
  const investorSort = useTableSort({ onSortChange: investorPagination.resetPage });

  // Brokers
  const brokerPagination = usePagination({ defaultRowsPerPage: 10 });
  const brokerSort = useTableSort({ onSortChange: brokerPagination.resetPage });

  // Collateral Providers
  const collateralPagination = usePagination({ defaultRowsPerPage: 10 });
  const collateralSort = useTableSort({ onSortChange: collateralPagination.resetPage });

  // Issuers
  const issuerPagination = usePagination({ defaultRowsPerPage: 10 });
  const issuerSort = useTableSort({ onSortChange: issuerPagination.resetPage });

  // Issuer ↔ Investors link dialog state
  const [linkDialog, setLinkDialog] = useState({ open: false, issuer: null, ids: [] });
  const [linkSaveError, setLinkSaveError] = useState(null);

  const {
    data: stakeholderData,
    reload: reloadStakeholders,
    setData: setStakeholderData,
  } = useAsyncData(
    useCallback(async () => {
      // Fetch the full list (search/sort/pagination happen client-side). The list
      // routes default to limit=25 server-side; without a high limit only the first
      // 25 rows (alphabetical) load and client-side search can't find the rest.
      const [investorsData, brokersData, providersData, issuersData, countriesData] = await Promise.all([
        apiGet('/stakeholders/investors?page=1&limit=100000').catch(() => ({ data: [] })),
        brokersEnabled
          ? apiGet('/stakeholders/brokers?page=1&limit=100000').catch(() => ({ data: [] }))
          : Promise.resolve({ data: [] }),
        collateralEnabled
          ? apiGet('/stakeholders/collateral-providers?page=1&limit=100000').catch(() => ({ data: [] }))
          : Promise.resolve({ data: [] }),
        issuersEnabled
          ? apiGet('/stakeholders/issuers?page=1&limit=100000').catch(() => ({ data: [] }))
          : Promise.resolve({ data: [] }),
        apiGet('/refdata/countries').catch(() => ({ data: [] })),
      ]);
      return {
        investors: investorsData?.data || [],
        brokers: brokersData?.data || [],
        providers: providersData?.data || [],
        issuers: issuersData?.data || [],
        countries: countriesData?.data || [],
      };
    }, [brokersEnabled, collateralEnabled, issuersEnabled]),
    { defaultValue: { investors: [], brokers: [], providers: [], issuers: [], countries: [] }, extractData: false }
  );

  const investors = stakeholderData.investors;
  const brokers = stakeholderData.brokers;
  const providers = stakeholderData.providers;
  const issuers = stakeholderData.issuers;
  const countries = stakeholderData.countries;

  // Entity-type chips (OR within the selected set) + domicile-country dropdown, both
  // AND-combined with the free-text search. Applies to every tab.
  const passesTypeCountry = useCallback((row) => (
    (typeFilters.length === 0 || typeFilters.includes(row.type))
    && (countryFilter === 'all' || row.domicile_country_id === countryFilter)
  ), [typeFilters, countryFilter]);

  const makeSearchMatcher = useCallback(() => {
    const term = searchTerm.toLowerCase();
    return (value) => value && value.toString().toLowerCase().includes(term);
  }, [searchTerm]);

  const filteredInvestors = useMemo(() => {
    const matches = makeSearchMatcher();
    return investors.filter(i =>
      passesTypeCountry(i) &&
      (!searchTerm || matches(i.name) || matches(i.email) || matches(i.client_code) || matches(i.type))
    );
  }, [investors, searchTerm, makeSearchMatcher, passesTypeCountry]);

  const filteredBrokers = useMemo(() => {
    const matches = makeSearchMatcher();
    return brokers.filter(b =>
      passesTypeCountry(b) &&
      (!searchTerm || matches(b.name) || matches(b.contact_person) || matches(b.phone) || matches(b.type))
    );
  }, [brokers, searchTerm, makeSearchMatcher, passesTypeCountry]);

  const filteredProviders = useMemo(() => {
    const matches = makeSearchMatcher();
    return providers.filter(p =>
      passesTypeCountry(p) &&
      (!searchTerm || matches(p.name) || matches(p.contact_person) || matches(p.phone) || matches(p.type) || matches(p.credit_rating))
    );
  }, [providers, searchTerm, makeSearchMatcher, passesTypeCountry]);

  const filteredIssuers = useMemo(() => {
    const matches = makeSearchMatcher();
    return issuers.filter(i =>
      passesTypeCountry(i) &&
      (!searchTerm || matches(i.name) || matches(i.contact_person) || matches(i.phone) || matches(i.type) || matches(i.email) || matches(i.site))
    );
  }, [issuers, searchTerm, makeSearchMatcher, passesTypeCountry]);

  // Distinct entity-type values actually present per tab — drives that tab's chip set so
  // each stakeholder kind shows only its own real types (investors have a fixed enum;
  // brokers/providers/issuers use a free type, so derive from data).
  const investorTypeOptions = useMemo(
    () => [...new Set(investors.map(i => i.type).filter(Boolean))].sort(), [investors]);
  const brokerTypeOptions = useMemo(
    () => [...new Set(brokers.map(b => b.type).filter(Boolean))].sort(), [brokers]);
  const providerTypeOptions = useMemo(
    () => [...new Set(providers.map(p => p.type).filter(Boolean))].sort(), [providers]);
  const issuerTypeOptions = useMemo(
    () => [...new Set(issuers.map(i => i.type).filter(Boolean))].sort(), [issuers]);

  const filtersActive = Boolean(searchTerm) || typeFilters.length > 0 || countryFilter !== 'all';
  const enabledTabs = useMemo(() => {
    const countLabel = (filtered, total) =>
      filtersActive && filtered !== total ? `${filtered}/${total}` : `${total}`;
    const tabs = [{ key: 'investors', label: `Investors (${countLabel(filteredInvestors.length, investors.length)})` }];
    if (brokersEnabled) {
      tabs.push({ key: 'brokers', label: `Brokers (${countLabel(filteredBrokers.length, brokers.length)})` });
    }
    if (collateralEnabled) {
      tabs.push({ key: 'collateral', label: `Collateral Providers (${countLabel(filteredProviders.length, providers.length)})` });
    }
    if (issuersEnabled) {
      tabs.push({ key: 'issuers', label: `${issuerLabel}s (${countLabel(filteredIssuers.length, issuers.length)})` });
    }
    return tabs;
  }, [investors.length, brokers.length, providers.length, issuers.length, filteredInvestors.length, filteredBrokers.length, filteredProviders.length, filteredIssuers.length, filtersActive, brokersEnabled, collateralEnabled, issuersEnabled, issuerLabel]);

  const tabIndex = enabledTabs.findIndex(t => t.key === tabKey);

  // Reset filters when switching tabs — the type chips are tab-specific and only one tab
  // is visible at a time, so a leftover type/country filter from another tab would confuse.
  const handleTabChange = (key) => {
    setTabKey(key);
    setSearchTerm('');
    setTypeFilters([]);
    setCountryFilter('all');
  };

  const toggleTypeFilter = (t) => {
    setTypeFilters((prev) => (prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]));
  };

  const resetFilters = () => {
    setSearchTerm('');
    setTypeFilters([]);
    setCountryFilter('all');
  };

  // Reset pagination when any filter changes
  useEffect(() => {
    investorPagination.resetPage();
    brokerPagination.resetPage();
    collateralPagination.resetPage();
    issuerPagination.resetPage();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- resetPage is stable; pagination objects intentionally omitted
  }, [searchTerm, typeFilters, countryFilter]);

  const handleOpenDialog = (type, item = null, mode = 'edit') => {
    setStakeholderType(type);
    setEditingItem(item);
    setDialogMode(mode);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingItem(null);
  };

  const handleSaved = () => {
    // List refresh only — the dialog manages its own close lifecycle now.
    reloadStakeholders();
  };

  const handleDelete = useCallback((type, id) => {
    confirm.show({
      title: 'Delete Stakeholder',
      message: 'Are you sure you want to delete this stakeholder?',
      confirmText: 'Delete',
      confirmColor: 'error',
      onConfirm: async () => {
        try {
          let endpoint = '';
          if (type === 'investor') endpoint = '/stakeholders/investors';
          if (type === 'broker') endpoint = '/stakeholders/brokers';
          if (type === 'provider') endpoint = '/stakeholders/collateral-providers';
          if (type === 'issuer') endpoint = '/stakeholders/issuers';

          await apiDelete(`${endpoint}/${id}`);
          await reloadStakeholders();
        } catch (err) {
          logger.error('Error deleting stakeholder', { message: err.message, status: err.response?.status });
          notify.error('Failed to delete stakeholder: ' + (err.response?.data?.error?.message || err.response?.data?.message || err.message));
        }
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reloadStakeholders is stable; wrapping in useCallback for useMemo column deps
  }, [confirm.show, reloadStakeholders, notify]);

  const handleOpenLinkInvestors = useCallback(async (issuer) => {
    try {
      const detail = await apiGet(`/stakeholders/issuers/${issuer.id}`);
      const ids = detail?.data?.linked_investor_ids || [];
      setLinkSaveError(null);
      setLinkDialog({ open: true, issuer, ids });
    } catch (err) {
      notify.error('Failed to load issuer investors: ' + (err.response?.data?.error?.message || err.message));
    }
  }, [notify]);

  const handleSaveInvestorLinks = useCallback(async () => {
    if (!linkDialog.issuer) return;
    try {
      await apiPut(`/stakeholders/issuers/${linkDialog.issuer.id}/investors`, { investor_ids: linkDialog.ids });
      setLinkDialog({ open: false, issuer: null, ids: [] });
      reloadStakeholders();
    } catch (err) {
      setLinkSaveError(err.response?.data?.error?.message || err.message || 'Save failed');
    }
  }, [linkDialog, reloadStakeholders]);

  // ─── Column definitions (filtered by feature flags) ──────────────────────
  const investorColumns = useMemo(() => [
    { key: 'id', header: 'ID', sortable: true, align: 'right', sx: { width: 72 }, getValue: (item) => item.id, render: (item) => item.id },
    { key: 'name', header: 'Name', sortable: true, getValue: (item) => item.name, render: (item) => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <BusinessIcon fontSize="small" color="action" />
        <Box>
          <Typography variant="body2" fontWeight="medium">{item.name}</Typography>
          {item.is_staff && (
            <Chip label="Staff" size="small" color="info" variant="outlined" sx={{ mt: 0.25 }} />
          )}
        </Box>
      </Box>
    )},
    { key: 'type', header: 'Type', sortable: true, getValue: (item) => item.type, render: (item) => item.type },
    { key: 'client_code', header: 'Client Code', featureKey: 'investors.identity_classification', sortable: true, getValue: (item) => item.client_code, render: (item) => (
      <Typography variant="body2">{item.client_code || '-'}</Typography>
    )},
    { key: 'email', header: 'Email', featureKey: 'investors.contact_details', sortable: true, getValue: (item) => item.email, render: (item) => (
      item.email ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <EmailIcon fontSize="small" color="action" />
          <Typography variant="body2">{item.email}</Typography>
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary">-</Typography>
      )
    )},
    { key: 'tax_classification', header: 'Tax Class.', featureKey: 'investors.kyc_aml', sortable: true, getValue: (item) => item.tax_classification, render: (item) => (
      item.tax_classification ? (
        <Chip label={item.tax_classification} size="small" variant="outlined" />
      ) : '-'
    )},
    { key: 'fica_status', header: 'FICA Status', featureKey: 'investors.kyc_aml', sortable: true, getValue: (item) => item.fica_status, render: (item) => (
      item.fica_status ? (
        <Chip
          label={item.fica_status.replace('_', ' ')}
          color={getFicaColor(item.fica_status)}
          size="small"
        />
      ) : '-'
    )},
    { key: 'wht_rate', header: 'WHT Rate', featureKey: 'investors.wht_override', sortable: true, getValue: (item) => item.wht_rate != null ? parseFloat(item.wht_rate) : 0, render: (item) => (
      item.wht_rate ? `${parseFloat(item.wht_rate).toFixed(2)}%` : '0%'
    )},
    { key: 'status', header: 'Status', sortable: true, getValue: (item) => item.active ? 1 : 0, render: (item) => (
      <Chip
        label={item.active ? 'Active' : 'Inactive'}
        color={item.active ? 'success' : 'default'}
        size="small"
      />
    )},
    { key: 'actions', header: 'Actions', align: 'center', render: (item) => (
      <Box onClick={(e) => e.stopPropagation()} sx={{ display: 'inline-flex' }}>
        <IconButton size="small" title="Open" onClick={() => handleOpenDialog('investor', item, 'view')}>
          <EditIcon fontSize="small" />
        </IconButton>
        {canAdmin && (
          <IconButton size="small" color="error" onClick={() => handleDelete('investor', item.id)}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
    )},
  ].filter(col => !col.featureKey || isFeatureEnabled('stakeholder_management', col.featureKey)),
  [isFeatureEnabled, canAdmin, handleDelete]);


  const brokerColumns = useMemo(() => [
    { key: 'id', header: 'ID', sortable: true, align: 'right', sx: { width: 72 }, getValue: (item) => item.id, render: (item) => item.id },
    { key: 'name', header: 'Name', sortable: true, getValue: (item) => item.name, render: (item) => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <BusinessIcon fontSize="small" color="action" />
        <Typography variant="body2" fontWeight="medium">{item.name}</Typography>
      </Box>
    )},
    { key: 'type', header: 'Type', sortable: true, getValue: (item) => item.type, render: (item) => item.type },
    { key: 'contact_person', header: 'Contact Person', featureKey: 'brokers.contact_info', sortable: true, getValue: (item) => item.contact_person, render: (item) => item.contact_person || '-' },
    { key: 'phone', header: 'Phone', featureKey: 'brokers.contact_info', sortable: true, getValue: (item) => item.phone, render: (item) => item.phone || '-' },
    { key: 'license_status', header: 'License Status', featureKey: 'brokers.regulatory_licensing', sortable: true, getValue: (item) => item.license_status, render: (item) => (
      item.license_status ? (
        <Chip
          label={item.license_status}
          color={getLicenseColor(item.license_status)}
          size="small"
        />
      ) : '-'
    )},
    { key: 'default_commission', header: 'Default Commission', featureKey: 'brokers.commission_structure', sortable: true, getValue: (item) => item.default_commission_rate != null && item.default_commission_rate !== '' ? parseFloat(item.default_commission_rate) : null, render: (item) => (
      item.default_commission_rate != null && item.default_commission_rate !== ''
        ? `${parseFloat(item.default_commission_rate).toFixed(4)}%`
        : '-'
    )},
    { key: 'preferred', header: 'Preferred', featureKey: 'brokers.commission_structure', sortable: true, getValue: (item) => item.is_preferred ? 1 : 0, render: (item) => (
      item.is_preferred ? <Chip label="Preferred" color="primary" size="small" /> : null
    )},
    { key: 'status', header: 'Status', sortable: true, getValue: (item) => item.active ? 1 : 0, render: (item) => (
      <Chip
        label={item.active ? 'Active' : 'Inactive'}
        color={item.active ? 'success' : 'default'}
        size="small"
      />
    )},
    { key: 'actions', header: 'Actions', align: 'center', render: (item) => (
      <Box onClick={(e) => e.stopPropagation()} sx={{ display: 'inline-flex' }}>
        {canWrite && (
          <IconButton size="small" onClick={() => handleOpenDialog('broker', item)}>
            <EditIcon fontSize="small" />
          </IconButton>
        )}
        {canAdmin && (
          <IconButton size="small" color="error" onClick={() => handleDelete('broker', item.id)}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
    )},
  ].filter(col => !col.featureKey || isFeatureEnabled('stakeholder_management', col.featureKey)),
  [isFeatureEnabled, canWrite, canAdmin, handleDelete]);


  const providerColumns = useMemo(() => [
    { key: 'id', header: 'ID', sortable: true, align: 'right', sx: { width: 72 }, getValue: (item) => item.id, render: (item) => item.id },
    { key: 'name', header: 'Name', sortable: true, getValue: (item) => item.name, render: (item) => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <BusinessIcon fontSize="small" color="action" />
        <Typography variant="body2" fontWeight="medium">{item.name}</Typography>
      </Box>
    )},
    { key: 'type', header: 'Type', sortable: true, getValue: (item) => item.type, render: (item) => item.type },
    { key: 'contact_person', header: 'Contact Person', featureKey: 'collateral.contact_info', sortable: true, getValue: (item) => item.contact_person, render: (item) => item.contact_person || '-' },
    { key: 'phone', header: 'Phone', featureKey: 'collateral.contact_info', sortable: true, getValue: (item) => item.phone, render: (item) => item.phone || '-' },
    { key: 'credit_rating', header: 'Credit Rating', featureKey: 'collateral.credit_capacity', sortable: true, getValue: (item) => item.credit_rating, render: (item) => item.credit_rating || '-' },
    { key: 'exposure_limit', header: 'Exposure Limit', featureKey: 'collateral.credit_capacity', sortable: true, getValue: (item) => item.total_exposure_limit != null ? parseFloat(item.total_exposure_limit) : null, render: (item) => formatCurrency(item.total_exposure_limit) },
    { key: 'status', header: 'Status', sortable: true, getValue: (item) => item.active ? 1 : 0, render: (item) => (
      <Chip
        label={item.active ? 'Active' : 'Inactive'}
        color={item.active ? 'success' : 'default'}
        size="small"
      />
    )},
    { key: 'actions', header: 'Actions', align: 'center', render: (item) => (
      <Box onClick={(e) => e.stopPropagation()} sx={{ display: 'inline-flex' }}>
        {canWrite && (
          <IconButton size="small" onClick={() => handleOpenDialog('provider', item)}>
            <EditIcon fontSize="small" />
          </IconButton>
        )}
        {canAdmin && (
          <IconButton size="small" color="error" onClick={() => handleDelete('provider', item.id)}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
    )},
  ].filter(col => !col.featureKey || isFeatureEnabled('stakeholder_management', col.featureKey)),
  [isFeatureEnabled, canWrite, canAdmin, handleDelete, formatCurrency]);


  const issuerColumns = useMemo(() => [
    { key: 'id', header: 'ID', sortable: true, align: 'right', sx: { width: 72 }, getValue: (item) => item.id, render: (item) => item.id },
    { key: 'name', header: 'Name', sortable: true, getValue: (item) => item.name, render: (item) => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <BusinessIcon fontSize="small" color="action" />
        <Typography variant="body2" fontWeight="medium">{item.name}</Typography>
      </Box>
    )},
    { key: 'type', header: 'Type', sortable: true, getValue: (item) => item.type, render: (item) => item.type },
    { key: 'site', header: 'Site', sortable: true, getValue: (item) => item.site || '', render: (item) => (
      item.site ? (
        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{item.site}</Typography>
      ) : '-'
    )},
    { key: 'contact_person', header: 'Contact Person', sortable: true, getValue: (item) => item.contact_person, render: (item) => item.contact_person || '-' },
    { key: 'email', header: 'Email', sortable: true, getValue: (item) => item.email, render: (item) => (
      item.email ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <EmailIcon fontSize="small" color="action" />
          <Typography variant="body2">{item.email}</Typography>
        </Box>
      ) : '-'
    )},
    { key: 'total_investors', header: 'Investors', sortable: true, getValue: (item) => item.total_investors ?? 0, render: (item) => (
      <Chip label={item.total_investors ?? 0} size="small" variant="outlined" />
    )},
    { key: 'total_contracts', header: 'Contracts', sortable: true, getValue: (item) => item.total_contracts ?? 0, render: (item) => (
      <Chip label={item.total_contracts ?? 0} size="small" variant="outlined" />
    )},
    { key: 'status', header: 'Status', sortable: true, getValue: (item) => item.active ? 1 : 0, render: (item) => (
      <Chip
        label={item.active ? 'Active' : 'Inactive'}
        color={item.active ? 'success' : 'default'}
        size="small"
      />
    )},
    { key: 'actions', header: 'Actions', align: 'center', render: (item) => (
      <Box onClick={(e) => e.stopPropagation()} sx={{ display: 'inline-flex' }}>
        {canWrite && (
          <IconButton size="small" title={`Link investors`} onClick={() => handleOpenLinkInvestors(item)}>
            <BusinessIcon fontSize="small" />
          </IconButton>
        )}
        {canWrite && (
          <IconButton size="small" onClick={() => handleOpenDialog('issuer', item)}>
            <EditIcon fontSize="small" />
          </IconButton>
        )}
        {canAdmin && (
          <IconButton size="small" color="error" onClick={() => handleDelete('issuer', item.id)}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
    )},
  ],
  [canWrite, canAdmin, handleDelete, handleOpenLinkInvestors]);


  // ─── Main render ──────────────────────────────────────────────────────────
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Stakeholder Management</Typography>
      </Box>

      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabIndex >= 0 ? tabIndex : 0} onChange={(e, v) => handleTabChange(enabledTabs[v].key)}>
          {enabledTabs.map(tab => (
            <Tab key={tab.key} label={tab.label} />
          ))}
        </Tabs>
      </Paper>

      {/* Investors Tab */}
      {tabKey === 'investors' && (
        <>
          <StakeholderFilterBar
            searchTerm={searchTerm}
            onSearch={setSearchTerm}
            searchPlaceholder="Name, email, client code..."
            typeOptions={investorTypeOptions}
            selectedTypes={typeFilters}
            onToggleType={toggleTypeFilter}
            countries={countries}
            countryValue={countryFilter}
            onCountry={setCountryFilter}
            onReset={resetFilters}
            actions={(
              <Box sx={{ display: 'flex', gap: 1 }}>
                {canAdmin && investors.length > 1 && (
                  <Button
                    variant="outlined"
                    startIcon={<MergeTypeIcon />}
                    onClick={() => setMergeDialogOpen(true)}
                  >
                    Merge investors
                  </Button>
                )}
                {canWrite && (
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenDialog('investor')}
                  >
                    Add Investor
                  </Button>
                )}
              </Box>
            )}
          />
          <DataTable
            columns={investorColumns}
            data={filteredInvestors}
            pagination={investorPagination}
            sort={investorSort}
            emptyMessage={searchTerm ? 'No investors match your search' : 'No investors found'}
            onRowClick={(item) => handleOpenDialog('investor', item, 'view')}
          />
        </>
      )}

      {/* Brokers Tab */}
      {tabKey === 'brokers' && (
        <>
          <StakeholderFilterBar
            searchTerm={searchTerm}
            onSearch={setSearchTerm}
            searchPlaceholder="Name, contact person, phone..."
            typeOptions={brokerTypeOptions}
            selectedTypes={typeFilters}
            onToggleType={toggleTypeFilter}
            countries={countries}
            countryValue={countryFilter}
            onCountry={setCountryFilter}
            onReset={resetFilters}
            actions={canWrite && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog('broker')}
              >
                Add Broker
              </Button>
            )}
          />
          <DataTable
            columns={brokerColumns}
            data={filteredBrokers}
            pagination={brokerPagination}
            sort={brokerSort}
            emptyMessage={searchTerm ? 'No brokers match your search' : 'No brokers found'}
            onRowClick={canWrite ? (item) => handleOpenDialog('broker', item) : undefined}
          />
        </>
      )}

      {/* Collateral Providers Tab */}
      {tabKey === 'collateral' && (
        <>
          <StakeholderFilterBar
            searchTerm={searchTerm}
            onSearch={setSearchTerm}
            searchPlaceholder="Name, contact person, rating..."
            typeOptions={providerTypeOptions}
            selectedTypes={typeFilters}
            onToggleType={toggleTypeFilter}
            countries={countries}
            countryValue={countryFilter}
            onCountry={setCountryFilter}
            onReset={resetFilters}
            actions={canWrite && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog('provider')}
              >
                Add Collateral Provider
              </Button>
            )}
          />
          <DataTable
            columns={providerColumns}
            data={filteredProviders}
            pagination={collateralPagination}
            sort={collateralSort}
            emptyMessage={searchTerm ? 'No collateral providers match your search' : 'No collateral providers found'}
            onRowClick={canWrite ? (item) => handleOpenDialog('provider', item) : undefined}
          />
        </>
      )}

      {/* Issuers Tab */}
      {tabKey === 'issuers' && (
        <>
          <StakeholderFilterBar
            searchTerm={searchTerm}
            onSearch={setSearchTerm}
            searchPlaceholder="Name, contact person, email..."
            typeOptions={issuerTypeOptions}
            selectedTypes={typeFilters}
            onToggleType={toggleTypeFilter}
            countries={countries}
            countryValue={countryFilter}
            onCountry={setCountryFilter}
            onReset={resetFilters}
            actions={canWrite && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog('issuer')}
              >
                Add {issuerLabel}
              </Button>
            )}
          />
          <DataTable
            columns={issuerColumns}
            data={filteredIssuers}
            pagination={issuerPagination}
            sort={issuerSort}
            emptyMessage={searchTerm ? `No ${issuerLabel.toLowerCase()}s match your search` : `No ${issuerLabel.toLowerCase()}s found`}
            onRowClick={canWrite ? (item) => handleOpenDialog('issuer', item) : undefined}
          />
        </>
      )}

      <StakeholderFormDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        onSaved={handleSaved}
        stakeholderType={stakeholderType}
        editingItem={editingItem}
        countries={countries}
        initialMode={canWrite ? dialogMode : 'view'}
      />

      {/* Merge duplicate investors */}
      <MergeInvestorDialog
        open={mergeDialogOpen}
        investors={investors}
        onClose={() => setMergeDialogOpen(false)}
        onMerged={({ survivor, loser }) => {
          notify.success(`Merged "${loser.name}" into "${survivor.name}".`);
          reloadStakeholders();
        }}
      />

      {/* Issuer ↔ Investors link dialog */}
      <Dialog
        open={linkDialog.open}
        onClose={() => setLinkDialog({ open: false, issuer: null, ids: [] })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Linked Investors — {linkDialog.issuer?.name || ''}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select investors that belong to this {issuerLabel.toLowerCase()}. Membership is permanent and survives contract maturity.
          </Typography>
          <Autocomplete
            multiple
            options={investors}
            value={investors.filter(inv => linkDialog.ids.includes(inv.id))}
            onChange={(_, newVal) => setLinkDialog(d => ({ ...d, ids: newVal.map(v => v.id) }))}
            getOptionLabel={(opt) => opt.name || ''}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            renderInput={(params) => (
              <TextField {...params} label="Investors" placeholder="Search investors..." />
            )}
          />
          {linkSaveError && (
            <Typography color="error" variant="body2" sx={{ mt: 2 }}>{linkSaveError}</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLinkDialog({ open: false, issuer: null, ids: [] })}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveInvestorLinks}>Save</Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog {...confirm.dialogProps} />
    </Box>
  );
}

export default StakeholderManagement;
